<template>
  <modal v-bind="[$props, $attrs]" @modal-close="emitCloseEvent" class="payment-modal">
    <h2 class="modal-title">
      <slot name="title"></slot>
    </h2>
    <transition name="fade" mode="out-in">
      <div v-if="$props.amountSlotVisible && paymentCards.length" :key="'customAmountSlot'">
        <slot name="amount"></slot>
      </div>
      <div v-else :key="'paymentSlot'">
        <transition name="fade" mode="out-in">
          <div v-if="paymentCards.length > 0 && !dataLoading && !$props.loading">
            <p class="modal-description">
              <slot name="description"></slot>
            </p>
            <transition name="fade" mode="out-in">
              <payment-modal-payment-choice v-if="!paymentChoiceSelection.length"
                                            @selected-choice="handlePaymentChoice"/>
              <div v-else>
                <transition-group name="fade" tag="div" class="payment-cards-container">
                  <div v-if="paymentChoiceSelection.includes('savedCard')" :key="'savedCards'"
                       class="payment-cards-panel">
                    <h3 class="payment-modal-subtitle">{{ $t('paymentModal.paymentChoice.savedCards') }}</h3>
                    <div v-for="(card, idx) in paymentCards"
                         :key="'card'+idx"
                         class="saved-card-wrapper">
                      <base-radio v-model="selectedCard"
                                  :value="card.code"
                                  name="selectedCard"
                                  position="right"
                                  vertical-align="center"
                                  class="radio-single">
                <span class="card-label">
                  <img class="card-image" :src="getCardImage(card.type)" alt="">
                  {{ $t('paymentModal.creditCard') + getShorterCardNumber(card.cardNumber) }}
                </span>
                      </base-radio>
                    </div>
                    <div class="button-container">
                      <base-button variant="primary"
                                   class="pay-button"
                                   :disabled="!selectedCard"
                                   @click="emitPayWithCard">
                        {{ $t('common.pay') }}
                      </base-button>
                    </div>
                  </div>
                  <div v-if="paymentChoiceSelection.includes('newCard')" :key="'newCard'"
                       class="payment-cards-panel">
                    <h3 class="payment-modal-subtitle">{{ $t('paymentModal.paymentChoice.newCard') }}</h3>
                    <p class="payment-modal-description">{{ $t('paymentModal.newCardSaveDescription') }}</p>
                    <div class="button-container">
                      <base-checkbox variant="green"
                                     v-model="rememberCard"
                                     :label="$t('paymentModal.rememberCard')"
                                     size="small"
                                     class="remember-me-check"/>
                      <base-button variant="primary"
                                   class="pay-button"
                                   @click="emitPayWithNewCard">
                        {{ $t('common.continue') }}
                      </base-button>
                    </div>
                  </div>
                </transition-group>
              </div>
            </transition>
          </div>
          <div v-else-if="!paymentCards.length && !dataLoading && !$props.loading" class="no-cards-container">
            <p class="no-cards-description" v-if="!$props.amountSlotVisible">
              <slot name="noCardDescription"></slot>
            </p>
            <div class="actions-container" :class="{'reverse': $props.reverseActions || $props.amountSlotVisible}">
              <template v-if="$props.amountSlotVisible">
                <slot name="amount"></slot>
              </template>
              <div class="actions-checkbox">
                <base-checkbox variant="green"
                               v-model="rememberCard"
                               :label="$t('paymentModal.rememberCardData')"
                               size="small"
                               class="remember-me-check"/>
                <p class="remember-description">
                  {{ $t('paymentModal.noCardRememberDescription') }}
                </p>
              </div>
              <div class="payment-modal-divider"></div>
              <div class="button-container" v-if="!$props.amountSlotVisible">
                <base-button variant="primary"
                             class="continue-button"
                             @click="emitPayWithNewCard">
                  {{ $t('common.continue') }}
                </base-button>
                <base-button v-if="$props.hasCancelButton"
                             variant="text"
                             class="cancel-button"
                             @click="emitCloseEvent">
                  {{ $t('common.cancel') }}
                </base-button>
              </div>
            </div>
          </div>
          <base-inline-loader v-else/>
        </transition>
      </div>
    </transition>
    <slot name="special" v-if="$props.amountSlotVisible !== undefined && paymentCards.length"></slot>
  </modal>
</template>

<script>
import Modal from '@/components/common/modal/modal'
import PaymentModalPaymentChoice from './components/payment-modal-payment-choice'
import cardsConfig from './mixins/cardsConfig'
import { CARD_IMAGES } from './mixins/cardsImages'

export default {
  name: 'payment-modal',
  inheritAttrs: false,
  data () {
    return {
      paymentCards: [],
      dataLoading: false,
      paymentCardsFetched: false,
      paymentChoiceSelection: [],
      selectedCard: '',
      rememberCard: false
    }
  },
  props: {
    visible: Boolean,
    loading: Boolean,
    reverseActions: Boolean,
    hasCancelButton: Boolean,
    amountSlotVisible: Boolean
  },
  mixins: [cardsConfig],
  components: { PaymentModalPaymentChoice, Modal },
  methods: {
    fetchPaymentCards () {
      this.dataLoading = true
      this.$apiRequest.post('user/payment/cards')
        .then(response => {
          this.paymentCards = response.data.cards || []
          if (this.paymentCards.length) {
            // resize listener nam treba samo ako ima sačuvane kartice
            window.addEventListener('resize', this.handleResize)
          }
        })
        .catch(e => {
          // should we display errors?
          this.handleErrorMessage(e.response.data)
        })
        .finally(() => {
          this.paymentCardsFetched = true
          this.dataLoading = false
        })
    },
    resetData () {
      this.paymentChoiceSelection = []
      this.selectedCard = ''
    },
    emitCloseEvent () {
      this.resetData()
      window.removeEventListener('resize', this.handleResize)

      this.$emit('close')
    },
    handlePaymentChoice (data) {
      this.paymentChoiceSelection = []
      setTimeout(() => {
        this.paymentChoiceSelection.push(data)
      }, 0)
    },
    handleResize () {
      if (window.innerWidth >= 768) {
        this.paymentChoiceSelection = this.paymentCardChoice.map(choice => choice.value)
      } else {
        if (this.paymentChoiceSelection.length >= 2) {
          // ako ima dva elementa u nizu, verovatno je došao na mob rezoluciju tako što je smanjio prozor, pa mu resetujemo sve
          // i prikazujemo izbor kako zeli da plati
          this.paymentChoiceSelection = []
        }
      }
    },
    getShorterCardNumber (number) {
      return number.slice(-8)
    },
    getCardImage (type) {
      return CARD_IMAGES[type] ? `/assets/images/cards/${CARD_IMAGES[type]}` : `/assets/images/cards/${CARD_IMAGES.DEFAULT}`
    },
    emitPayWithCard () {
      this.$emit('payment-with-saved-card', this.selectedCard)
    },
    emitPayWithNewCard () {
      this.$emit('payment-with-new-card', { remember: this.rememberCard })
    }
  },
  watch: {
    visible (newVal) {
      if (newVal && !this.paymentCardsFetched) {
        this.fetchPaymentCards()
      }
      if (newVal) {
        this.handleResize()
        // resize listener nam treba samo ako ima sačuvane kartice
        window.addEventListener('resize', this.handleResize)
      }
    }
  },
  created () {
    this.handleResize()
  }
}
</script>

<style scoped lang="scss" src="./styles/payment-modal.scss"></style>
